import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';
import { useMemo } from 'react';

// ROLLBACK(last-price): (Mark)/(Last) 라벨 재활성화 시 복원.
// import { isMarkTriggerPriceType } from '@flipster/apis/v2/trade/types';
import { LeverageBadge, OrderSideBadge } from '@flipster/features/src/misc/badge';
import { AdjustOrderPopover } from '@flipster/features/src/shared/position/adjust-order/popover';
import {
  getPendingOrderPriceDisplayType,
  getPendingPriceDisplayType,
  isPendingPriceEditable,
} from '@flipster/features/src/shared/position/pending-order-roles';
import { usePendingTpsl } from '@flipster/features/src/shared/position/use-pending-tpsl';
import { useSnapshotSpec } from '@flipster/features/src/shared/snapshot/use-snapshot-spec';
import { useAmendOrder, useClosePosition, useOffHoursAmendGuard } from '@flipster/features/src/shared/trade-actions';
import { FormattedMessage } from '@flipster/i18n';
import { accountBunja } from '@flipster/r230522/original/account';
import type { PendingPosition } from '@flipster/r230522/original/order';
import { TextButton } from '@flipster/react/design-system/text-button';
import { Datetime } from '@flipster/react/design-utils/datetime';
import Formatted from '@flipster/react/design-utils/formatted';
import { css } from '@flipster/styled-system/css';
import { flex, hstack } from '@flipster/styled-system/patterns';
import { Button } from '@flipster/ui';
import { TableCell, TableRow } from '@flipster/ui/Table';
import { getQuoteCurrencyPrecision } from '@flipster/utils/constants/currency';
import Num, { countDecimalPlaces } from '@flipster/utils/num';

import { SymbolIconCell } from '@web/app/[locale]/(web)/trade/_misc/etc-section/symbol-icon-cell';

import { calcPendingOrderNotional, useSymbolMarginType } from '../../../../../../order-form/perpetual/bunja/base';
import TimeTriggerTpSlCol from '../../common/time-trigger-tpsl-col';
import TpSlCol from '../../common/tpsl-col';
import { pendingPositionTableBunja } from '../../state';

interface Props {
  item: PendingPosition;
  index: number;
  isOneWayMode: boolean;
}

const TriggerOrderRow = ({ item, index, isOneWayMode }: Props) => {
  const t = useTranslations();
  const transCommon = useTranslations('Common.r250401');
  const transTrade = useTranslations('Trade.r250401');
  const {
    side,
    slot,
    symbol,
    amount,
    initLeverage,
    triggerPrice,
    // triggerPriceType, // ROLLBACK(last-price)
    orderTpTriggerPrice,
    orderSlTriggerPrice,
    orderTpTriggerPriceRatio,
    orderSlTriggerPriceRatio,
    qty,
    triggerTimestamp,
    isReduceOnly,
    price,
  } = item;

  const marginType = useSymbolMarginType(symbol);

  const { v2AccountAtom } = useBunja(accountBunja);
  const { hasAnyTriggerTimeAtom, showAllSymbolAtom } = useBunja(pendingPositionTableBunja);
  const account = useAtomValue(v2AccountAtom);
  const hasAnyTriggerTime = useAtomValue(hasAnyTriggerTimeAtom);
  const showAllSymbol = useAtomValue(showAllSymbolAtom);

  const { tpslDisplay } = usePendingTpsl(item);
  const { instrumentsKeyBySymbol } = useSnapshotSpec();
  const { cancelPosition, isCancelLoading: isLoading } = useClosePosition();
  const { amendOrder } = useAmendOrder();
  // off-hours amend 알림은 팝오버 열기 전 게이트로 (reduce-only/TPSL exempt).
  const guardAmendOpen = useOffHoursAmendGuard(item);

  const orderNotional = useMemo(() => calcPendingOrderNotional(item), [item]);

  // ROLLBACK(last-price): (Mark)/(Last) 라벨 — MM 준비 완료 시 재활성화.
  // const triggerTypeParenLabel = useMemo(() => {
  //   return isMarkTriggerPriceType(triggerPriceType)
  //     ? t('Autogen_common.r251126.s_Mark')
  //     : t('Common.r260623.Last_price_short');
  // }, [triggerPriceType, t]);
  //
  // const triggerTypeSuffix = (
  //   <span className={css({ textStyle: 'Text/S/Regular', color: 'content.default.level-2' })}>
  //     ({triggerTypeParenLabel})
  //   </span>
  // );
  const triggerTypeSuffix = null;

  const instrument = instrumentsKeyBySymbol?.[symbol];
  if (!instrument) return null;

  const staticOrderPriceLabel = (label: string) => (
    <span className={css({ textStyle: 'Text/S/Regular', color: 'content.default.level-4' })}>{label}</span>
  );

  const renderOrderPrice = () => {
    // 이 칸(발동 후 예정 가격)은 항상 read-only다.
    switch (getPendingOrderPriceDisplayType(item)) {
      case 'bbo':
        return staticOrderPriceLabel(t('Autogen_common.r251126.s_BBO'));
      case 'price':
        return (
          <Formatted.Number
            value={price}
            precision={instrument.pricePrecision}
            toFixed
            css={{ textStyle: 'Text/S/Monospace/Regular', color: 'content.default.level-5' }}
          />
        );
      case 'market':
        return staticOrderPriceLabel(transTrade('Tab_market'));
    }
  };

  const renderTpslColumns = () => {
    if (tpslDisplay === 'placeholder') {
      return (
        <>
          <TableCell>-</TableCell>
          <TableCell>-</TableCell>
        </>
      );
    }

    if (triggerTimestamp) {
      return (
        <>
          <TimeTriggerTpSlCol
            positionType='pending'
            side={side}
            tpSlType='tp'
            symbol={symbol}
            slot={slot}
            priceRatio={orderTpTriggerPriceRatio}
            initLeverage={initLeverage}
            triggerTimestamp={triggerTimestamp}
            amount={amount}
            qty={qty}
          />
          <TimeTriggerTpSlCol
            positionType='pending'
            side={side}
            tpSlType='sl'
            symbol={symbol}
            slot={slot}
            priceRatio={orderSlTriggerPriceRatio}
            initLeverage={initLeverage}
            triggerTimestamp={triggerTimestamp}
            amount={amount}
            qty={qty}
          />
        </>
      );
    }

    return (
      <>
        <TpSlCol
          positionType='pending'
          parentId={item.orderId}
          side={side}
          tpSlType='tp'
          symbol={symbol}
          price={orderTpTriggerPrice}
          precision={instrument.pricePrecision}
          initLeverage={initLeverage}
          fundsInvested={amount}
          tradeMode={account?.tradeMode}
          showAllSymbol={showAllSymbol}
          isReduceOnly={isReduceOnly}
          marginType={marginType}
        />
        <TpSlCol
          positionType='pending'
          parentId={item.orderId}
          side={side}
          tpSlType='sl'
          symbol={symbol}
          price={orderSlTriggerPrice}
          precision={instrument.pricePrecision}
          initLeverage={initLeverage}
          fundsInvested={amount}
          tradeMode={account?.tradeMode}
          showAllSymbol={showAllSymbol}
          isReduceOnly={isReduceOnly}
          marginType={marginType}
        />
      </>
    );
  };

  return (
    <TableRow>
      <SymbolIconCell symbol={symbol} tradeType='perpetual' index={index} />
      <TableCell>
        <div className={hstack({ gap: 4 })}>
          <OrderSideBadge orderSide={side} hideText />
          {!isReduceOnly && <LeverageBadge leverage={initLeverage} />}
        </div>
      </TableCell>
      <TableCell>
        {getPendingPriceDisplayType(item) === 'market' ? (
          // 타입: time-trigger 주문 → 체결 시점 시장가 라벨
          <span className={css({ textStyle: 'Text/S/Regular', color: 'content.default.level-5' })}>
            {transTrade('Market_price_at_trigger_time')}
          </span>
        ) : isPendingPriceEditable(item) ? (
          // 편집 가능: Trigger price → 편집 popover
          <div className={flex({ gap: 4, align: 'end' })}>
            <AdjustOrderPopover
              symbol={symbol}
              side={side}
              initLeverage={initLeverage}
              marginType={marginType}
              isLimit={false}
              currency={instrument.currency}
              quoteCurrency={instrument.quoteCurrency}
              pricePrecision={instrument.pricePrecision}
              qtyPrecision={countDecimalPlaces(instrument.unitOrderQty)}
              priceLabel={`${transTrade('Trigger_price')} (${instrument.quoteCurrency})`}
              priceValue={triggerPrice ?? ''}
              qtyValue={qty}
              onBeforeOpen={guardAmendOpen}
              onConfirm={(newPrice, sizeValue, sizeMode) =>
                amendOrder({
                  symbol,
                  orderId: item.orderId,
                  triggerPrice: newPrice,
                  side,
                  sizeMode,
                  sizeValue,
                  leverage: initLeverage,
                })
              }
            >
              <TextButton
                color='neutral'
                size='small'
                label={<Formatted.Number value={triggerPrice} precision={instrument.pricePrecision} toFixed />}
              />
            </AdjustOrderPopover>
            {triggerTypeSuffix}
          </div>
        ) : (
          // 편집 불가: read-only 숫자
          <div className={flex({ gap: 4, align: 'end' })}>
            <Formatted.Number
              value={triggerPrice}
              precision={instrument.pricePrecision}
              toFixed
              css={{ textStyle: 'Text/S/Monospace/Regular', color: 'content.default.level-5' }}
            />
            {triggerTypeSuffix}
          </div>
        )}
      </TableCell>
      <TableCell>{renderOrderPrice()}</TableCell>
      <TableCell>
        <div className={flex({ direction: 'column', alignItems: 'flex-start' })}>
          {!Num(qty).isZero() &&
            (triggerTimestamp ? (
              <Formatted.Number
                value={qty}
                precision={countDecimalPlaces(instrument.unitOrderQty)}
                toFixed
                abs
                suffix={instrument.currency}
                css={{ textStyle: 'Text/S/Monospace/Regular', color: 'content.default.level-1' }}
              />
            ) : (
              <AdjustOrderPopover
                symbol={symbol}
                side={side}
                initLeverage={initLeverage}
                marginType={marginType}
                isLimit={false}
                currency={instrument.currency}
                quoteCurrency={instrument.quoteCurrency}
                pricePrecision={instrument.pricePrecision}
                qtyPrecision={countDecimalPlaces(instrument.unitOrderQty)}
                priceLabel={`${transTrade('Trigger_price')} (${instrument.quoteCurrency})`}
                priceValue={triggerPrice ?? ''}
                qtyValue={qty}
                onBeforeOpen={guardAmendOpen}
                onConfirm={(newPrice, sizeValue, sizeMode) =>
                  amendOrder({
                    symbol,
                    orderId: item.orderId,
                    triggerPrice: newPrice,
                    side,
                    sizeMode,
                    sizeValue,
                    leverage: initLeverage,
                  })
                }
              >
                <TextButton
                  color='neutral'
                  size='small'
                  label={
                    <Formatted.Number
                      value={qty}
                      precision={countDecimalPlaces(instrument.unitOrderQty)}
                      toFixed
                      abs
                      suffix={instrument.currency}
                    />
                  }
                />
              </AdjustOrderPopover>
            ))}
          {!Num(amount).isZero() && (
            <Formatted.Number
              value={orderNotional}
              toFixed
              abs
              precision={getQuoteCurrencyPrecision(instrument.quoteCurrency)}
              suffix={instrument.quoteCurrency}
              css={{
                textStyle: 'Text/S/Monospace/Regular',
                color: Num(qty).isZero() ? 'content.default.level-5' : 'content.default.level-2',
              }}
            />
          )}
        </div>
      </TableCell>
      {hasAnyTriggerTime && (
        <TableCell>
          <Datetime
            value={triggerTimestamp}
            format='MMM dd, yyyy HH:mm:ss'
            css={{ textStyle: 'Text/S/Regular', color: 'content.default.level-5' }}
          />
        </TableCell>
      )}
      {renderTpslColumns()}
      {isOneWayMode && (
        <TableCell>
          <span className={css({ textStyle: 'Text/S/Regular', color: 'content.default.level-5' })}>
            {isReduceOnly ? (
              <FormattedMessage id='Autogen_common.r251126.s_Yes' />
            ) : (
              <FormattedMessage id='Autogen_common.r251126.s_No' />
            )}
          </span>
        </TableCell>
      )}
      <TableCell align='right'>
        <Button size='small' shape='tertiary' loading={isLoading} onClick={() => cancelPosition(item, 'perpetual')}>
          {transCommon('Cancel')}
        </Button>
      </TableCell>
    </TableRow>
  );
};

export default TriggerOrderRow;
